import { IChecklistCategory, IChecklistItem, IChecklistPopulated, IChecklistProgress } from "@mctypes/checklist.types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
export interface IChecklistReducer {
  checklist: IChecklistPopulated;
  checklistCategories: IChecklistCategory[];
  checklistItems: IChecklistItem[];
  progress: IChecklistProgress;
  activeTab: string;
  checklistTableVariant: ChecklistTableVariant | null;
  activeProjectChecklistsCreatedByLoggedInUser: IChecklistPopulated[];
  freeTextSearch: string;
  selectedPnsFilterItem: string;
  hiddenColumns: string[];
  refactoredEditMode: boolean;
  tempHiddenCategories: string[];
  refactoredCollapsedCategoryIds: string[];
  refactoredExpandedCategoryIds: string[];
  mcTemplateTestPage: {
    selectedTemplateNrId: number | null;
  };
  showInDevelopment: boolean;
}

export const CHECKLIST_TABLE_VARIANT_STORAGE_KEY = "checklistTableVariant";
export type ChecklistTableVariant = "legacy" | "refactored";

const savedChecklistTableVariant = localStorage.getItem(CHECKLIST_TABLE_VARIANT_STORAGE_KEY);

const initialState: IChecklistReducer = {
  checklist: null,
  checklistItems: [],
  checklistCategories: [],
  activeTab: "CHECKLIST",
  checklistTableVariant:
    savedChecklistTableVariant === "refactored" || savedChecklistTableVariant === "legacy"
      ? savedChecklistTableVariant
      : null,
  progress: {
    accepted: 0,
    rejected: 0,
  },
  activeProjectChecklistsCreatedByLoggedInUser: [],
  freeTextSearch: "",
  selectedPnsFilterItem: null,
  hiddenColumns: [],
  refactoredEditMode: false,
  tempHiddenCategories: [],
  refactoredCollapsedCategoryIds: [],
  refactoredExpandedCategoryIds: [],
  mcTemplateTestPage: {
    selectedTemplateNrId: null,
  },
  showInDevelopment: false,
};

export const checklistSlice = createSlice({
  name: "checklist",
  initialState,
  reducers: {
    resetChecklistInRedux: (state) => {
      Object.assign(state, initialState);
    },
    addChecklistCategoryInRedux: (state, action: PayloadAction<IChecklistCategory>) => {
      state.checklistCategories = [...state.checklistCategories, action.payload];
    },
    addChecklistItemInRedux: (state, action: PayloadAction<IChecklistItem>) => {
      state.checklistItems = [...state.checklistItems, action.payload];
    },
    setActiveChecklistTabInRedux: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    setChecklistTableVariantInRedux: (state, action: PayloadAction<ChecklistTableVariant>) => {
      state.checklistTableVariant = action.payload;
      localStorage.setItem(CHECKLIST_TABLE_VARIANT_STORAGE_KEY, action.payload);
    },
    setCurrentChecklistInRedux: (state, action: PayloadAction<IChecklistPopulated>) => {
      state.checklist = action.payload;
    },
    setChecklistCategoriesInRedux: (state, action: PayloadAction<IChecklistCategory[]>) => {
      state.checklistCategories = action.payload.map((cat) => {
        const prev = state.checklistCategories.find((prevCat) => prevCat._id === cat._id);
        const collapsed = cat.collapsed ?? false;
        return { ...cat, collapsed };
      });
    },
    setChecklistItemsInRedux: (state, action: PayloadAction<IChecklistItem[]>) => {
      state.checklistItems = action.payload;
    },
    updateChecklistInRedux: (state, action: PayloadAction<IChecklistPopulated>) => {
      state.checklist = action.payload;
    },
    updateChecklistCategoryInRedux: (state, action: PayloadAction<IChecklistCategory[]>) => {
      state.checklistCategories = action.payload;
    },
    updateChecklistItemsInRedux: (state, action: PayloadAction<IChecklistItem[]>) => {
      state.checklistItems = action.payload;
    },
    updateChecklistProgressInRedux: (state, action: PayloadAction<IChecklistProgress>) => {
      state.progress = action.payload ?? {
        accepted: 0,
        rejected: 0,
      };
    },
    deleteChecklistCategoryInRedux: (state, action: PayloadAction<string>) => {
      state.checklistCategories = state.checklistCategories.filter((category) => category._id !== action.payload);
    },
    deleteChecklistItemsInRedux: (state, action: PayloadAction<string>) => {
      state.checklistItems = state.checklistItems.filter((item) => item._id !== action.payload);
    },
    toggleAllCategoryCollapseInRedux: (state, action: PayloadAction<boolean>) => {
      state.checklistCategories = state.checklistCategories.map((cat) => {
        return { ...cat, collapsed: action.payload };
      });
    },
    toggleTempCollapseAllCategoriesInRedux: (
      state,
      action: PayloadAction<{ cat: IChecklistCategory[]; hidden: string[] }>,
    ) => {
      state.tempHiddenCategories = action.payload.hidden;
      state.checklistCategories = action.payload.cat;
    },
    toggleCategoryCollapseInRedux: (state, action: PayloadAction<string>) => {
      state.checklistCategories = state.checklistCategories.map((cat) => {
        if (cat._id === action.payload) {
          return { ...cat, collapsed: cat.collapsed !== true };
        }
        return cat;
      });
    },
    setRefactoredCollapsedCategoryIdsInRedux: (state, action: PayloadAction<string[]>) => {
      state.refactoredCollapsedCategoryIds = action.payload;
    },
    setRefactoredExpandedCategoryIdsInRedux: (state, action: PayloadAction<string[]>) => {
      state.refactoredExpandedCategoryIds = action.payload;
    },
    setRefactoredEditModeInRedux: (state, action: PayloadAction<boolean>) => {
      state.refactoredEditMode = action.payload;
    },
    updateSingleChecklistItemInRedux: (state, action: PayloadAction<IChecklistItem>) => {
      state.checklistItems = state.checklistItems.map((item) =>
        item._id === action.payload?._id ? action.payload : item,
      );
    },
    setSelectedPnsFilterItemInRedux: (state, action: PayloadAction<string>) => {
      state.selectedPnsFilterItem = action.payload;
    },
    setChecklistFreeTextSearchInRedux: (state, action: PayloadAction<string>) => {
      state.freeTextSearch = action.payload;
    },
    toggleColVisibility: (state, action: PayloadAction<{ keys: string[]; bool: boolean }>) => {
      action.payload.keys.forEach((key) => {
        if (action.payload.bool === false) {
          if (!state.hiddenColumns.includes(key)) {
            state.hiddenColumns.push(key);
          }
        } else {
          state.hiddenColumns = state.hiddenColumns.filter((column) => column !== key);
        }
      });
    },
    setSelectedTemplateNrIdInRedux: (state, action: PayloadAction<number | null>) => {
      state.mcTemplateTestPage.selectedTemplateNrId = action.payload;
    },
    setShowChecklistTemplatesInDevelopmentInRedux: (state, action: PayloadAction<boolean>) => {
      state.showInDevelopment = action.payload;
    },
  },
});

export const {
  addChecklistCategoryInRedux,
  addChecklistItemInRedux,
  setCurrentChecklistInRedux,
  updateChecklistProgressInRedux,
  setActiveChecklistTabInRedux,
  setChecklistTableVariantInRedux,
  setChecklistItemsInRedux,
  setChecklistCategoriesInRedux,
  updateChecklistInRedux,
  updateChecklistCategoryInRedux,
  updateChecklistItemsInRedux,
  deleteChecklistCategoryInRedux,
  deleteChecklistItemsInRedux,
  toggleAllCategoryCollapseInRedux,
  toggleCategoryCollapseInRedux,
  updateSingleChecklistItemInRedux,
  setSelectedPnsFilterItemInRedux,
  setChecklistFreeTextSearchInRedux,
  toggleColVisibility,
  toggleTempCollapseAllCategoriesInRedux,
  setRefactoredCollapsedCategoryIdsInRedux,
  setRefactoredExpandedCategoryIdsInRedux,
  setRefactoredEditModeInRedux,
  resetChecklistInRedux,
  setSelectedTemplateNrIdInRedux,
  setShowChecklistTemplatesInDevelopmentInRedux,
} = checklistSlice.actions;

export default checklistSlice.reducer;
